import { useCallback, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import classNames from 'classnames';

import {
  getEventName,
  getTrackerId,
  POSTHOG_CAPTURE_ATTRIBUTES,
  POSTHOG_PROPERTIES,
  TRACKER_IDS,
} from '../../../config/tracker';
import { useFirebaseAuth } from '../../../../global/FirebaseProvider/FirebaseProvider';
import ChevronUpSvg from '../../../global/SvgComponents/ChevronUp_svg';
import IconLockCircle from '../../../images/icon-lock-circle.svg';
import { getGiftCardBrandValues } from '../../../utils/giftCards';
import { insertTCIcon, TBB_TC_ICON } from '../../../utils/tbbRegistration';

import './GiftCardItem.scss';
import { formatReadableNumber } from '../../../utils/numbers';

const {
  giftCards: {
    list: {
      giftCardItem: trackerGiftCardItem,
    },
  },
} = TRACKER_IDS;

export const GIFT_CARD_ITEM_TYPE = {
  DEFAULT: 'default',
  EXTENDED: 'extended',
};

const GiftCardItem = ({
  type = GIFT_CARD_ITEM_TYPE.DEFAULT,
  giftCardItem,
  onClick,
  trackerProps = {},
  containerClassName,
}) => {
  const { userData } = useFirebaseAuth();
  const posthog = usePostHog();

  const {
    uid,
    name,
    giftCardUrl,
  } = useMemo(() => (
    giftCardItem
  ), [giftCardItem]);

  const isExtended = type === GIFT_CARD_ITEM_TYPE.EXTENDED;

  const {
    minDenomination,
    maxDenomination,
    minBalance,
    disabled,
  } = useMemo(() => (
    getGiftCardBrandValues({
      giftCardBrand: giftCardItem,
      currentBalance: userData?.balance?.currentBalance,
    })
  ), [giftCardItem]);

  const handleOnClick = useCallback(() => {
    posthog.capture(
      getEventName(trackerGiftCardItem),
      {
        [POSTHOG_PROPERTIES.GIFT_CARD_ID]: uid,
        [POSTHOG_PROPERTIES.GIFT_CARD_NAME]: name,
      },
    );

    onClick?.(giftCardItem);
  }, [onClick, giftCardItem]);

  return (
    <div className={classNames('GiftCardItem', type, { 'non-actionable': !onClick, disabled }, containerClassName)}>
      {!isExtended && disabled && (
        <img
          className="GiftCardItem__gem"
          src={IconLockCircle}
          alt="gift card locked"
        />
      )}
      <button
        className={classNames('GiftCardItem__caret', { disabled })}
        id={`GiftCardItem__caret-${uid}`}
        onClick={handleOnClick}
        type="button"
        aria-label="gift card item caret"
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleOnClick();
          }
        }}
        {...{
          [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerGiftCardItem),
          [POSTHOG_CAPTURE_ATTRIBUTES.GIFT_CARD_ID]: uid,
          [POSTHOG_CAPTURE_ATTRIBUTES.GIFT_CARD_NAME]: name,
          ...trackerProps,
        }}
      >
        <div className="GiftCardItem__caret--cover">
          <img
            className="GiftCardItem__caret--cover-logo"
            src={giftCardUrl}
            alt="brand logo"
          />
        </div>
        <div className="GiftCardItem__caret--content">
          <div className="GiftCardItem__caret--content-text">
            {isExtended
              ? `$${minDenomination} Gift Card`
              : `$${minDenomination} - $${maxDenomination}`}
          </div>
          {!isExtended && (
            <div className="GiftCardItem__caret--content-arrow">
              <ChevronUpSvg
                color="var(--trashie-ui-faded-black-80)"
                width={10}
              />
            </div>
          )}
        </div>
        {!isExtended && (
          <div className="GiftCardItem__caret--cta">
            <span
              className="GiftCardItem__caret--cta-label"
              title={disabled ? `Starting at ${formatReadableNumber(minBalance)} TC` : ''}
            >
              {disabled
                ? insertTCIcon(`Starting at ${TBB_TC_ICON} ${formatReadableNumber(minBalance)}`, 16, 'var(--membership-lime)')
                : 'Get Gift Card'}
            </span>
          </div>
        )}
      </button>
    </div>
  );
};

export default GiftCardItem;
